/**
 * Reading a `MissionRun` in `REVIEW.DAT`'s own terms.
 *
 * `debrief-review.ts` grades a sortie against 52 sentences that talk about
 * *"the enemy fighters"*, *"our reconnaissance aircraft"*, *"our bombers"* and
 * *"our %t"*. Which of the mission's flights and targets those phrases name
 * depends on the mission family, so the translation lives here rather than in
 * the rules — the rules stay readable beside the prose they implement, and the
 * one awkward part of the job is in one place.
 *
 * Split out of `debrief.ts` because that file is already near the 400-line gate,
 * and because this is a different kind of code: `debrief.ts` fills in a screen's
 * fields, this decides what a sentence is counting.
 *
 * Counts come from the **mission's own accounting** wherever the mission keeps
 * one — a `protect` objective's `required`/`progress`, a `RaidOutcome`'s bomber
 * and target tallies — and fall back to the encounter roster only where it does
 * not. That ordering matters: the objective is what the briefing promised, and
 * the debriefing is a report against the briefing.
 */

import type { EncounterState } from "../combat/encounter.ts";
import type { TargetKind } from "../combat/target-kinds.ts";
import { NO_REVIEW_FACTS, type ReviewFacts } from "./debrief-review.ts";
import type { MissionRun, MissionStats } from "./mission-run.ts";
import type { MissionType } from "./mission-types.ts";
import type { ObjectiveState } from "./objectives.ts";

/** `%t`'s plain English, per target kind. The nouns the manual and the raid briefings use. */
export const TARGET_NOUNS: Readonly<Record<TargetKind, string>> = {
  balloon: "balloon",
  zeppelin: "Zeppelin",
  supplyDump: "supply dump",
  factory: "factory",
  railYard: "rail yard",
  train: "train",
  searchlight: "searchlight",
};

/**
 * Whether the flight met the enemy at all, which only DOGFIGHT and the ace duel
 * ask. **A proxy, and marked as one**: `PS.EXE`'s own test is not decoded, so
 * this asks whether anything happened that requires the two sides to have found
 * each other — a shot fired, a hit taken, a victory, a loss. A patrol that flew
 * out and back without seeing anything answers `false`, which is what
 * *"Your flight did not locate the specified enemy fighters"* describes.
 */
function madeContact(stats: MissionStats): boolean {
  return (
    stats.roundsFired > 0 ||
    stats.hitsTaken > 0 ||
    stats.friendlyLosses > 0 ||
    stats.aircraftKills + stats.balloonKills + stats.zeppelinKills + stats.squadronKills > 0
  );
}

function hostileAircraft(encounter: EncounterState): { total: number; destroyed: number } {
  const hostile = encounter.aircraft.filter((aircraft) => aircraft.side === "hostile");
  return { total: hostile.length, destroyed: hostile.filter((aircraft) => aircraft.status === "destroyed").length };
}

function targetsOfKind(
  encounter: EncounterState,
  side: "friendly" | "hostile",
  kind: TargetKind,
): { total: number; destroyed: number } {
  const matching = encounter.targets.filter((target) => target.side === side && target.kind === kind);
  return { total: matching.length, destroyed: matching.filter((target) => target.destroyed).length };
}

/**
 * The `protect` objective's own tally, as a total and a loss. `ObjectiveState`
 * counts survivors (`progress`) against the number that had to live
 * (`required`), so the loss is the difference — the mission's accounting, not a
 * recount of the roster.
 */
function escortFromObjective(run: MissionRun): { total: number; lost: number } | undefined {
  const protect = run.spec.objectives.find((objective) => objective.kind === "protect" || objective.kind === "protectTargets");
  if (!protect) return undefined;
  const state: ObjectiveState | undefined = run.objectives.find((entry) => entry.id === protect.id);
  if (!state || state.required <= 0) return undefined;
  return { total: state.required, lost: Math.max(0, state.required - state.progress) };
}

/** The ground target `%t` names, preferring the one the mission actually flew against. */
function targetNoun(encounter: EncounterState, type: MissionType): string {
  const wanted = type === "stopBombers" || type === "interceptZeppelin" ? "friendly" : "hostile";
  const structural = encounter.targets.find(
    (target) => target.side === wanted && (target.kind === "supplyDump" || target.kind === "factory" || target.kind === "railYard"),
  );
  if (structural) return TARGET_NOUNS[structural.kind];
  const any = encounter.targets.find((target) => target.side === wanted && target.kind !== "searchlight");
  return any ? TARGET_NOUNS[any.kind] : NO_REVIEW_FACTS.targetNoun;
}

/**
 * A `MissionRun`, in the terms the 52 messages are written in.
 *
 * The per-family branches are the whole point of the module: *"the enemy"* is
 * hostile aeroplanes to a DOGFIGHT, hostile balloons to BALLOON BUSTING and the
 * inbound bomber formation to STOP A BOMBING RAID, and each sentence is only
 * true of the one the mission was about.
 */
export function reviewFactsFrom(run: MissionRun): ReviewFacts {
  const { stats, encounter } = run;
  const type = run.spec.type;
  const raid = stats.raid;
  const escort = escortFromObjective(run);
  const base: ReviewFacts = {
    ...NO_REVIEW_FACTS,
    madeContact: madeContact(stats),
    friendlyLosses: stats.friendlyLosses,
    friendlyTargetsLost: stats.friendlyGroundTargetsLost,
    zeppelinDown: stats.zeppelinKills > 0,
    playerLost: stats.playerStatus === "destroyed",
    targetNoun: targetNoun(encounter, type),
  };

  if (type === "balloonBusting") {
    const balloons = targetsOfKind(encounter, "hostile", "balloon");
    return { ...base, enemyTotal: balloons.total, enemyDestroyed: balloons.destroyed };
  }

  if (type === "balloonDefense") {
    const ours = targetsOfKind(encounter, "friendly", "balloon");
    return { ...base, escortTotal: escort?.total ?? ours.total, escortLost: escort?.lost ?? ours.destroyed };
  }

  if (type === "escortBombers") {
    // The bombers are the charges and the enemy structure is the point of the
    // sortie, so both tallies come off the raid rather than off the roster.
    return {
      ...base,
      escortTotal: raid?.bombersLaunched ?? escort?.total ?? 0,
      escortLost: raid ? Math.max(0, raid.bombersLaunched - raid.bombersSurviving) : (escort?.lost ?? 0),
      targetsDestroyed: raid?.targetsDestroyed ?? stats.groundTargetsDestroyed,
    };
  }

  if (type === "stopBombers") {
    // Here the bombers are the *enemy*, and the target they were after is ours.
    const hostile = hostileAircraft(encounter);
    return {
      ...base,
      enemyTotal: raid?.bombersLaunched ?? hostile.total,
      enemyDestroyed: raid ? Math.max(0, raid.bombersLaunched - raid.bombersSurviving) : hostile.destroyed,
      friendlyTargetsLost: raid?.targetsDestroyed ?? stats.friendlyGroundTargetsLost,
    };
  }

  if (type === "escortRecon") {
    return { ...base, escortTotal: escort?.total ?? 0, escortLost: escort?.lost ?? 0 };
  }

  if (type === "destroyZeppelin" || type === "interceptZeppelin") {
    // Graded on the airship and on our `%t`; no aeroplane count is read.
    return base;
  }

  const hostile = hostileAircraft(encounter);
  return { ...base, enemyTotal: hostile.total, enemyDestroyed: hostile.destroyed };
}
